import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import useRecentDrawers from '@/hooks/useRecentDrawers';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  LayoutDashboard, Search, Fingerprint, Boxes, Layers, GitBranch, Eye,
  Clock, MapPin, ScrollText, Wrench, Plug, Archive, ClipboardCheck, FilePieChart,
  ExternalLink, X, ShieldCheck, Info, History, Trash2, ChevronLeft, ChevronRight,
  ShieldHalf, ArrowLeft,
} from 'lucide-react';

export const TRUST_URL = 'https://trust.apexverity.io';

const SEV_DOT = {
  critical: 'bg-apex-critical',
  high: 'bg-apex-warning',
  medium: 'bg-sky-400',
  low: 'bg-emerald-400',
  enforced: 'bg-apex-lens',
};

function timeAgo(ts) {
  if (!ts) return 'just now';
  const diff = Math.max(0, Date.now() - ts);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const NAV = [
  { section: 'OVERVIEW', items: [
    { label: 'Command Center', to: '/lens', icon: LayoutDashboard },
    { label: 'Investigations', to: '/lens/investigations', icon: Search, badge: '3 active' },
  ]},
  { section: 'IDENTITIES & INFRASTRUCTURE', items: [
    { label: 'Identity Proof', to: '/lens/identity', icon: Fingerprint, badge: '47 critical' },
    { label: 'Container Governance', to: '/lens/containers', icon: Boxes, badge: '9 actions' },
    { label: 'Account Landscape', to: '/lens/accounts', icon: MapPin },
  ]},
  { section: 'GOVERNANCE', items: [
    { label: 'Environment Integrity', to: '/lens/environment', icon: Layers, badge: '6 findings' },
    { label: 'Change Legitimacy', to: '/lens/changes', icon: GitBranch, badge: '7 bypasses' },
    { label: 'Missing Evidence', to: '/lens/missing-evidence', icon: Eye, badge: '4 alerts' },
  ]},
  { section: 'FORENSICS', items: [
    { label: 'Time Travel', to: '/lens/reconstruction', icon: Clock },
    { label: 'Governance Debt', to: '/lens/debt', icon: ScrollText },
  ]},
  { section: 'EVIDENCE', items: [
    { label: 'Evidence Vault', to: '/lens/evidence', icon: Archive },
    { label: 'Audit Workspace', to: '/lens/audit', icon: ClipboardCheck },
    { label: 'Board Reports', to: '/reports', icon: FilePieChart },
  ]},
  { section: 'PLATFORM', items: [
    { label: 'Integrations', to: '/lens/integrations', icon: Plug },
    { label: 'Remediation', to: '/lens/remediation', icon: Wrench },
  ]},
];

// NavLinkItem — single nav row, supports both expanded & collapsed states
function NavLinkItem({ item, active, collapsed, onClick }) {
  const Icon = item.icon;

  const inner = (
    <Link
      to={item.to}
      onClick={onClick}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`group relative flex items-center ${collapsed ? 'justify-center w-10 h-10 mx-auto' : 'gap-3 px-3 h-9'}
        rounded-full transition-colors text-[13px]
        ${active
          ? 'bg-slate-800/90 text-white'
          : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-100'}`}
    >
      {/* Active indicator dot — subtle left edge */}
      {active && !collapsed && (
        <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-4 rounded-full bg-apex-lens" />
      )}
      <Icon className={`shrink-0 ${collapsed ? 'w-[18px] h-[18px]' : 'w-[16px] h-[16px]'} ${active ? 'text-white' : 'text-slate-500 group-hover:text-slate-200'}`} strokeWidth={1.8} />
      {!collapsed && (
        <>
          <span className="flex-1 truncate font-medium">{item.label}</span>
          {item.badge && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-md font-mono font-medium tracking-tight
                ${item.badge.includes('critical') || item.badge.includes('alerts') || item.badge.includes('bypass')
                  ? 'bg-apex-critical/15 text-rose-300'
                  : 'bg-slate-700/70 text-slate-300'}`}
            >
              {item.badge}
            </span>
          )}
        </>
      )}
      {/* Collapsed badge dot — top-right */}
      {collapsed && item.badge && (
        <span className={`absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full
          ${item.badge.includes('critical') || item.badge.includes('alerts') || item.badge.includes('bypass')
            ? 'bg-apex-critical'
            : 'bg-slate-500'}`} />
      )}
    </Link>
  );

  if (!collapsed) return inner;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{inner}</TooltipTrigger>
      <TooltipContent side="right" className="bg-apex-shell text-white border-slate-700 text-[12px]">
        <div className="flex items-center gap-2">
          <span className="font-medium">{item.label}</span>
          {item.badge && <span className="text-[10px] font-mono text-slate-400">· {item.badge}</span>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export default function Sidebar({ collapsed = false, mobile = false, onToggleCollapse }) {
  const { setSidebarOpen, setDataSecurityOpen } = useApp();
  const location = useLocation();
  const { list: recent, clear: clearRecent } = useRecentDrawers();
  const isCollapsed = collapsed && !mobile;

  return (
    <TooltipProvider delayDuration={250}>
      <aside
        data-testid="sidebar"
        className={`flex flex-col bg-apex-shell text-slate-300 border-r border-slate-800/80
          ${mobile ? 'h-full w-[280px]' : `h-screen sticky top-0 ${isCollapsed ? 'w-[72px]' : 'w-[248px]'} hidden md:flex transition-[width] duration-200`}`}
      >
        {/* Back to marketing site */}
        {!isCollapsed && (
          <Link
            to="/"
            data-testid="sidebar-back-to-marketing"
            className="flex items-center gap-1.5 px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.14em] text-slate-500 hover:text-slate-300 transition-colors font-mono"
          >
            <ArrowLeft className="w-3 h-3" strokeWidth={2} />
            apexverity.io
          </Link>
        )}

        {/* Brand block */}
        {mobile ? (
          <div className="flex items-center justify-between px-4 py-4 border-b border-slate-800/80">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-apex-lens/15 ring-1 ring-apex-lens/30 flex items-center justify-center">
                <ShieldHalf className="w-4 h-4 text-apex-lens" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-bold text-white tracking-tight leading-none">APEX Lens</div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">Governance Proof</div>
              </div>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              data-testid="sidebar-close"
              className="text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className={`${isCollapsed ? 'px-3 py-4' : 'px-4 py-4'} border-b border-slate-800/80`}>
            <Link to="/" className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-2.5'}`} data-testid="sidebar-brand">
              <div className="w-8 h-8 rounded-lg bg-apex-lens/15 ring-1 ring-apex-lens/30 flex items-center justify-center shrink-0">
                <ShieldHalf className="w-4 h-4 text-apex-lens" strokeWidth={2} />
              </div>
              {!isCollapsed && (
                <div className="min-w-0">
                  <div className="text-[13px] font-bold text-white tracking-tight leading-none">APEX Lens</div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">Governance Proof</div>
                </div>
              )}
            </Link>
          </div>
        )}

        {/* Nav */}
        <nav className={`flex-1 overflow-y-auto ${isCollapsed ? 'py-3' : 'py-4'} space-y-5`}>
          {NAV.map((sec) => (
            <div key={sec.section}>
              {!isCollapsed && (
                <div className="px-4 mb-1.5 text-[10px] font-semibold tracking-[0.16em] text-slate-500">
                  {sec.section}
                </div>
              )}
              {isCollapsed && <div className="mx-3 mb-2 h-px bg-slate-800/60" />}
              <ul className={`${isCollapsed ? 'space-y-1' : 'space-y-0.5 px-2'}`}>
                {sec.items.map((item) => {
                  const active = location.pathname === item.to || (item.to !== '/lens' && location.pathname.startsWith(item.to));
                  return (
                    <li key={item.to}>
                      <NavLinkItem
                        item={item}
                        active={active}
                        collapsed={isCollapsed}
                        onClick={() => mobile && setSidebarOpen(false)}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-slate-800/80">
          {/* Recent drawers — only visible when expanded */}
          {!isCollapsed && recent.length > 0 && (
            <div className="px-3 pt-3 pb-2">
              <div className="flex items-center justify-between mb-1.5 px-1">
                <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.16em] text-slate-500">
                  <History className="w-3 h-3" /> RECENT
                </div>
                <button
                  onClick={clearRecent}
                  data-testid="recent-drawers-clear"
                  title="Clear recent"
                  className="text-slate-600 hover:text-slate-300 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <ul className="space-y-0.5">
                {recent.map((r) => (
                  <li key={r.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link
                          to={r.path}
                          onClick={() => mobile && setSidebarOpen(false)}
                          data-testid={`recent-drawer-${r.id}`}
                          className="group flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-slate-400 hover:bg-slate-800/60 hover:text-white transition-colors"
                        >
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${SEV_DOT[r.severity] ?? 'bg-slate-600'}`} />
                          <span className="flex-1 truncate">{r.label}</span>
                          <span className="text-[9px] font-mono text-slate-600 group-hover:text-slate-400 truncate max-w-[60px]">{r.id}</span>
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side="right" align="start" className="bg-apex-shell text-white border-slate-700 max-w-[280px]">
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${SEV_DOT[r.severity] ?? 'bg-slate-500'}`} />
                            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">{r.severity ?? 'item'}</span>
                            <span className="text-slate-600">·</span>
                            <span className="text-[10px] font-mono text-slate-400">{r.id}</span>
                          </div>
                          <div className="text-[12px] font-medium leading-snug">{r.label}</div>
                          <div className="text-[10px] text-slate-500 font-mono pt-0.5">
                            opened {timeAgo(r.ts)}
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Bottom utility links — desktop expanded only */}
          {!isCollapsed && !mobile && (
            <div className="px-4 py-3 space-y-2">
              <a href={TRUST_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-trust" className="flex items-center justify-between text-[12px] text-indigo-400 hover:text-indigo-300 transition-colors">
                <span>Switch to APEX Trust</span><ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button onClick={() => setDataSecurityOpen(true)} data-testid="nav-data-security" className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors">
                <ShieldCheck className="w-3 h-3" />Data &amp; Security
              </button>
              <Link to="/lens/how-it-works" data-testid="nav-how-it-works" className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors">
                <Info className="w-3 h-3" />How It Works
              </Link>
            </div>
          )}

          {/* Mobile footer (always expanded layout) */}
          {mobile && (
            <div className="px-4 py-3 space-y-2">
              <a href={TRUST_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between text-[12px] text-indigo-400 hover:text-indigo-300 transition-colors">
                <span>Switch to APEX Trust</span><ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button onClick={() => setDataSecurityOpen(true)} className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors">
                <ShieldCheck className="w-3 h-3" />Data &amp; Security
              </button>
              <Link to="/lens/how-it-works" onClick={() => setSidebarOpen(false)} className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors">
                <Info className="w-3 h-3" />How It Works
              </Link>
            </div>
          )}

          {/* Collapse toggle — desktop only, full width */}
          {!mobile && (
            <button
              onClick={onToggleCollapse}
              data-testid="sidebar-collapse-toggle"
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className={`w-full border-t border-slate-800/80 ${isCollapsed ? 'h-10 flex items-center justify-center' : 'h-10 flex items-center justify-center gap-1.5 text-[11px] font-medium tracking-tight text-slate-500 hover:text-slate-200 hover:bg-slate-800/40 transition-colors'}`}
            >
              {isCollapsed ? (
                <ChevronRight className="w-4 h-4 text-slate-500 hover:text-slate-200 transition-colors" />
              ) : (
                <>
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span>Collapse</span>
                </>
              )}
            </button>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}
